import React from "react";
import { Link } from "react-router-dom";
import {
  Shield,
  BadgeCheck,
  DollarSign,
  Users,
  Sparkles,
  ArrowRight,
  Check,
  Camera,
  CalendarCheck,
  IdCard,
  Award,
  TrendingUp,
  Clock,
  PackageOpen,
  Gift,
} from "lucide-react";
import { VerifiedBadge, TrelPlusBadge } from "@/components/VerifiedBadge";
// Shared marketing-page tokens + tiny atoms (see components/marketing/primitives).
// BecomeProvider previously redeclared these inline; now Become Host/Provider
// + About share the same source-of-truth.
import {
  IVORY, NAVY, EMERALD, GOLD, OCEAN, CORAL,
  Eyebrow, TrustChip,
} from "@/components/marketing/primitives";

const HERO_IMG =
  "https://images.unsplash.com/photo-1559339352-11d035aa65de?auto=format&fit=crop&w=1600&q=80";
const HERO_IMG_2 =
  "https://images.unsplash.com/photo-1452587925148-ce544e77e70d?auto=format&fit=crop&w=900&q=80";
const HERO_IMG_3 =
  "https://images.unsplash.com/photo-1540555700478-4be289fbecef?auto=format&fit=crop&w=900&q=80";

/* --- Page ------------------------------------------------------------- */
export default function BecomeProvider() {
  return (
    <div data-testid="page-become-provider" style={{ background: IVORY }}>
      {/* ============== HERO ============== */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-12 sm:pt-20 pb-16 sm:pb-24">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            {/* Copy */}
            <div className="lg:col-span-7">
              <Eyebrow>For Concierges</Eyebrow>
              <h1
                className="font-serif mt-4 leading-[1.02] tracking-tight"
                style={{
                  color: NAVY,
                  fontSize: "clamp(2.5rem, 6vw, 4.5rem)",
                }}
                data-testid="provider-hero-headline"
              >
                Turn your ability to create
                <br className="hidden sm:block" /> amazing moments into{" "}
                <span style={{ color: EMERALD }}>income.</span>
              </h1>
              <p
                className="mt-5 sm:mt-6 text-base sm:text-lg max-w-xl leading-relaxed"
                style={{ color: "rgba(7,26,45,0.72)" }}
                data-testid="provider-hero-subtext"
              >
                Customers bring the vision. You bring it to life — and earn doing
                what you already love. If you know great places, great services,
                and how to make people feel taken care of, TREL is built for you.
              </p>

              {/* "Free to join" badge — clarifies that signup itself is free,
                  Elite ($60) and TREL+ ($29/mo) are optional upgrades. */}
              <div
                className="mt-5 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-[0.16em]"
                style={{
                  background: `${EMERALD}12`,
                  color: EMERALD,
                  border: `1px solid ${EMERALD}33`,
                }}
                data-testid="provider-free-to-join-pill"
              >
                <Check size={12} strokeWidth={3} /> Free to join · Elite + TREL+ optional
              </div>

              {/* CTAs */}
              <div className="mt-7 flex flex-wrap items-center gap-3">
                <Link
                  to="/signup"
                  className="btn-coral inline-flex items-center gap-2 text-base"
                  data-testid="provider-hero-cta-apply"
                >
                  Apply for free <ArrowRight size={18} />
                </Link>
                <Link
                  to="/how-it-works#providers"
                  className="btn-outline-emerald text-sm sm:text-base"
                  data-testid="provider-hero-cta-learn"
                >
                  How TREL works
                </Link>
              </div>

              {/* Trust chips */}
              <div className="mt-8 flex flex-wrap gap-2.5">
                <TrustChip icon={Shield} label="TREL Certified" accent={OCEAN} />
                <TrustChip icon={BadgeCheck} label="Identity verified" accent={OCEAN} />
                <TrustChip icon={Award} label="Premium network" accent={GOLD} />
              </div>
            </div>

            {/* Image collage */}
            <div className="lg:col-span-5">
              <div className="relative">
                <div
                  className="rounded-[28px] overflow-hidden shadow-luxury-lg border"
                  style={{ borderColor: "rgba(7,26,45,0.08)" }}
                >
                  <img
                    src={HERO_IMG}
                    alt="Private chef preparing a luxury plate"
                    className="w-full h-[360px] sm:h-[460px] object-cover"
                    loading="eager"
                  />
                </div>

                {/* Floating badge card */}
                <div
                  className="hidden sm:flex absolute -left-6 bottom-8 items-center gap-3 bg-white rounded-2xl px-4 py-3 shadow-luxury-lg border"
                  style={{ borderColor: "rgba(7,26,45,0.08)" }}
                >
                  <VerifiedBadge size="md" />
                  <div className="text-xs leading-tight" style={{ color: NAVY }}>
                    <div className="font-semibold">Identity reviewed</div>
                    <div style={{ color: "rgba(7,26,45,0.55)" }}>
                      TREL Certified Training completed
                    </div>
                  </div>
                </div>

                {/* Small accent image (desktop only) */}
                <div
                  className="hidden lg:block absolute -right-6 -top-6 w-36 h-36 rounded-2xl overflow-hidden shadow-luxury border-2"
                  style={{ borderColor: IVORY }}
                >
                  <img
                    src={HERO_IMG_2}
                    alt="Photographer at work"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div
                  className="hidden lg:block absolute -right-2 -bottom-8 w-32 h-32 rounded-2xl overflow-hidden shadow-luxury border-2"
                  style={{ borderColor: IVORY }}
                >
                  <img
                    src={HERO_IMG_3}
                    alt="Wellness experience"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============== WHY JOIN — 4 simple value cards (Phase 43) ==============
          Customer-facing emotional positioning: why a great host/planner/creator
          should turn that ability into income on TREL. Keeps to four short
          benefit cards so the page stays scannable. */}
      <section
        className="max-w-7xl mx-auto px-5 sm:px-8 pb-16 sm:pb-24"
        data-testid="provider-why-join"
      >
        <div className="max-w-2xl">
          <Eyebrow>Why join TREL</Eyebrow>
          <h2
            className="font-serif mt-3 leading-tight"
            style={{ color: NAVY, fontSize: "clamp(1.9rem, 3.4vw, 2.75rem)" }}
          >
            Customers bring ideas. You bring them to life.
          </h2>
          <p className="mt-4 text-base leading-relaxed" style={{ color: "rgba(7,26,45,0.72)" }}>
            People everywhere need help creating moments. If you know great
            places, services, and how to make people feel taken care of —
            you can be a TREL Concierge.
          </p>
        </div>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
          {[
            {
              icon: Sparkles,
              accent: CORAL,
              testid: "benefit-earn-knowledge",
              title: "Earn From Your Knowledge",
              body: "Use your creativity, connections, and planning skills to create experiences customers love.",
            },
            {
              icon: Clock,
              accent: EMERALD,
              testid: "benefit-work-your-way",
              title: "Work Your Way",
              body: "Accept requests that match your interests, schedule, and expertise.",
            },
            {
              icon: PackageOpen,
              accent: GOLD,
              testid: "benefit-no-inventory",
              title: "No Inventory Needed",
              body: "No storefront. No products. Create value by managing details and experiences.",
            },
            {
              icon: Gift,
              accent: OCEAN,
              testid: "benefit-free-to-start",
              title: "Free To Start",
              body: "Create your profile, complete training, and start receiving requests.",
            },
          ].map((c) => (
            <article
              key={c.title}
              data-testid={`provider-${c.testid}`}
              className="rounded-3xl bg-white border p-6 shadow-luxury hover:-translate-y-0.5 hover:shadow-luxury-lg transition-all duration-200"
              style={{ borderColor: "rgba(7,26,45,0.08)" }}
            >
              <div
                className="w-11 h-11 rounded-2xl inline-flex items-center justify-center"
                style={{ background: `${c.accent}15`, color: c.accent }}
              >
                <c.icon size={20}/>
              </div>
              <h3 className="font-serif text-lg mt-4 leading-tight" style={{ color: NAVY }}>
                {c.title}
              </h3>
              <p className="text-sm mt-2 leading-relaxed" style={{ color: "rgba(7,26,45,0.7)" }}>
                {c.body}
              </p>
            </article>
          ))}
        </div>
      </section>

      {/* ============== HOW CONCIERGES MAKE MONEY — 5-step flow (Phase 43)
          Simple, professional, flexible. Doesn't promise easy money — attracts
          quality Concierges who understand the value they bring. */}
      <section
        className="max-w-7xl mx-auto px-5 sm:px-8 pb-16 sm:pb-24"
        data-testid="provider-how-money"
      >
        <div className="text-center max-w-2xl mx-auto">
          <Eyebrow color={EMERALD}>How Concierges earn</Eyebrow>
          <h2
            className="font-serif mt-3 leading-tight"
            style={{ color: NAVY, fontSize: "clamp(1.9rem, 3.4vw, 2.75rem)" }}
          >
            Five quiet steps. One unforgettable moment.
          </h2>
        </div>
        <ol className="mt-10 grid md:grid-cols-5 gap-3 sm:gap-4">
          {[
            { n: "01", title: "Customer shares vision", body: "A guest tells TREL what they imagine." },
            { n: "02", title: "Matching request",       body: "You receive a request that fits your expertise." },
            { n: "03", title: "Custom plan or offer",   body: "Build a custom plan, offer, or proposal." },
            { n: "04", title: "Customer accepts",       body: "Bookings + payments handled by TREL." },
            { n: "05", title: "Deliver & earn",         body: "Run the experience. Get paid. Build reviews." },
          ].map((s) => (
            <li
              key={s.n}
              data-testid={`provider-money-step-${s.n}`}
              className="rounded-2xl bg-white border p-5"
              style={{ borderColor: "rgba(7,26,45,0.08)" }}
            >
              <div
                className="text-[11px] uppercase tracking-[0.22em] font-semibold"
                style={{ color: EMERALD }}
              >
                Step {s.n}
              </div>
              <div className="mt-2 font-serif text-base leading-snug" style={{ color: NAVY }}>
                {s.title}
              </div>
              <div className="mt-2 text-xs leading-relaxed" style={{ color: "rgba(7,26,45,0.65)" }}>
                {s.body}
              </div>
            </li>
          ))}
        </ol>
      </section>

      {/* ============== BUSINESS POSITIONING (Phase 30) ============== */}
      {/* Welcomes BOTH independent experts AND established businesses.
          TREL is positioned as a new booking + growth channel, NOT another
          listing site. Same Concierge brand language; no redesign. */}
      <section
        className="max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-20"
        data-testid="provider-business-section"
      >
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7">
            <Eyebrow color={GOLD}>For Businesses + Independents</Eyebrow>
            <h2
              className="font-serif mt-3 leading-tight"
              style={{ color: NAVY, fontSize: "clamp(1.9rem, 3.4vw, 2.75rem)" }}
            >
              Handle complete customer experiences.
            </h2>
            <p
              className="mt-5 text-base sm:text-lg leading-relaxed max-w-2xl"
              style={{ color: "rgba(7,26,45,0.72)" }}
            >
              TREL customers share what they want — a weekend, a celebration,
              a milestone. Your job as the Concierge: handle every detail
              end-to-end. Reservations, vendors, surprises, coordination —
              all of it. One trusted point of contact for the customer.
            </p>
            <div className="mt-5">
              <div className="text-[11px] uppercase tracking-[0.22em] font-semibold" style={{ color: EMERALD }}>
                Perfect for
              </div>
              <p className="mt-2 text-sm leading-relaxed" style={{ color: "rgba(7,26,45,0.78)" }}>
                Event companies · Nightlife teams · Travel planners · Private
                chefs · Transportation companies · Luxury services · Local
                experience companies
              </p>
            </div>
            <ul className="mt-7 grid sm:grid-cols-2 gap-x-6 gap-y-3 max-w-2xl text-sm" style={{ color: NAVY }}>
              {[
                "New customer discovery channel",
                "Receive organized customer requests",
                "Reduce planning back-and-forth",
                "Showcase experience portfolio",
                "Manage bookings",
                "Secure payments",
                "Build reviews and trust",
                "Grow with AI-powered tools",
              ].map((b) => (
                <li key={b} className="flex items-start gap-2">
                  <Check size={14} strokeWidth={3} className="mt-1 shrink-0" style={{ color: EMERALD }} />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                to="/signup?role=provider"
                className="btn-coral inline-flex items-center gap-2"
                data-testid="provider-business-cta-apply"
              >
                Join as Business or Individual <ArrowRight size={16} />
              </Link>
              <span className="text-xs font-semibold uppercase tracking-[0.16em]" style={{ color: EMERALD }}>
                Free to join
              </span>
            </div>
          </div>

          {/* Account-type cards — Individual + Business side by side */}
          <div className="lg:col-span-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div
              className="rounded-3xl border p-6 bg-white shadow-luxury"
              style={{ borderColor: "rgba(7,26,45,0.08)" }}
              data-testid="provider-business-card-individual"
            >
              <div
                className="w-10 h-10 rounded-2xl inline-flex items-center justify-center"
                style={{ background: `${EMERALD}15`, color: EMERALD }}
              >
                <Users size={18} />
              </div>
              <h3 className="font-serif text-xl mt-4" style={{ color: NAVY }}>
                Individual Concierge
              </h3>
              <p className="text-xs mt-2 leading-relaxed" style={{ color: "rgba(7,26,45,0.65)" }}>
                For independent experts who create unforgettable experiences.
              </p>
            </div>
            <div
              className="rounded-3xl border p-6 bg-white shadow-luxury"
              style={{ borderColor: "rgba(7,26,45,0.08)" }}
              data-testid="provider-business-card-business"
            >
              <div
                className="w-10 h-10 rounded-2xl inline-flex items-center justify-center"
                style={{ background: `${GOLD}20`, color: NAVY }}
              >
                <Sparkles size={18} />
              </div>
              <h3 className="font-serif text-xl mt-4" style={{ color: NAVY }}>
                Business / Experience Company
              </h3>
              <p className="text-xs mt-2 leading-relaxed" style={{ color: "rgba(7,26,45,0.65)" }}>
                For businesses that create, manage, or deliver premium experiences — nightlife,
                event planners, yacht, chefs, travel, transportation, photographers, corporate events.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============== BENEFIT CARDS ============== */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-16 sm:pb-24">
        <div className="max-w-2xl">
          <Eyebrow>The TREL Advantage</Eyebrow>
          <h2
            className="font-serif mt-3 leading-tight"
            style={{ color: NAVY, fontSize: "clamp(1.9rem, 3.4vw, 2.75rem)" }}
          >
            Built to help you earn more, with less friction.
          </h2>
        </div>

        <div className="mt-10 grid md:grid-cols-3 gap-5 sm:gap-6">
          {/* CARD 1 — TREL Verified */}
          <article
            className="group relative rounded-3xl bg-white border p-7 sm:p-8 shadow-luxury transition-all duration-300 hover:-translate-y-1 hover:shadow-luxury-lg flex flex-col"
            style={{ borderColor: "rgba(7,26,45,0.08)" }}
            data-testid="provider-benefit-verified"
          >
            <div
              className="w-12 h-12 rounded-2xl inline-flex items-center justify-center"
              style={{ background: `${OCEAN}15`, color: OCEAN }}
            >
              <Shield size={22} />
            </div>
            <h3
              className="font-serif text-2xl mt-5"
              style={{ color: NAVY }}
            >
              TREL Elite <span className="text-xs uppercase tracking-[0.18em] font-semibold" style={{ color: OCEAN }}>Optional</span>
            </h3>
            <p
              className="mt-3 text-sm leading-relaxed"
              style={{ color: "rgba(7,26,45,0.72)" }}
            >
              Optional upgrade for Concierges who want enhanced screening: identity verification + third-party background check + Elite badge for top placement.
            </p>

            {/* Verification fee callout */}
            <div
              className="mt-5 rounded-2xl px-4 py-3 flex items-baseline justify-between border"
              style={{
                background: `${OCEAN}0D`,
                borderColor: `${OCEAN}33`,
              }}
            >
              <div className="text-xs uppercase tracking-wider font-semibold" style={{ color: OCEAN }}>
                Optional · one-time
              </div>
              <div className="font-serif text-xl" style={{ color: NAVY }}>
                $60 Elite screening
              </div>
            </div>

            <ul className="mt-5 space-y-2.5 text-sm" style={{ color: NAVY }}>
              {[
                "Identity verification",
                "Background check completed",
                "Verified badge displayed",
                "Higher customer trust",
              ].map((item) => (
                <li key={item} className="flex items-start gap-2.5">
                  <span
                    className="mt-0.5 w-5 h-5 rounded-full inline-flex items-center justify-center flex-shrink-0"
                    style={{ background: OCEAN, color: "#fff" }}
                  >
                    <Check size={12} strokeWidth={3} />
                  </span>
                  <span style={{ color: "rgba(7,26,45,0.85)" }}>{item}</span>
                </li>
              ))}
            </ul>
          </article>

          {/* CARD 2 — Keep More */}
          <article
            className="group relative rounded-3xl border p-7 sm:p-8 shadow-luxury transition-all duration-300 hover:-translate-y-1 hover:shadow-luxury-lg flex flex-col"
            style={{
              background: `linear-gradient(155deg, ${EMERALD} 0%, #0a6b53 100%)`,
              borderColor: EMERALD,
              color: IVORY,
            }}
            data-testid="provider-benefit-earnings"
          >
            <div
              className="w-12 h-12 rounded-2xl inline-flex items-center justify-center"
              style={{ background: "rgba(255,248,238,0.14)", color: IVORY }}
            >
              <DollarSign size={22} />
            </div>
            <h3 className="font-serif text-2xl mt-5" style={{ color: IVORY }}>
              Keep More of What You Earn
            </h3>
            <p
              className="mt-3 text-sm leading-relaxed"
              style={{ color: "rgba(255,248,238,0.82)" }}
            >
              Keep 80% of every completed booking plus 100% of customer tips.
            </p>

            {/* Split visual */}
            <div className="mt-6 grid grid-cols-3 gap-2">
              <div className="rounded-xl p-3 bg-white/10 border border-white/15">
                <div className="text-[10px] uppercase tracking-wider opacity-75">You earn</div>
                <div className="font-serif text-2xl mt-1">80%</div>
              </div>
              <div className="rounded-xl p-3 bg-white/10 border border-white/15">
                <div className="text-[10px] uppercase tracking-wider opacity-75">Tips</div>
                <div className="font-serif text-2xl mt-1">100%</div>
              </div>
              <div className="rounded-xl p-3 bg-white/5 border border-white/10">
                <div className="text-[10px] uppercase tracking-wider opacity-70">TREL fee</div>
                <div className="font-serif text-2xl mt-1 opacity-80">20%</div>
              </div>
            </div>

            <div className="mt-5 text-xs opacity-80">
              Same-day payouts after each completed booking.
            </div>
          </article>

          {/* CARD 3 — Premium customers */}
          <article
            className="group relative rounded-3xl bg-white border p-7 sm:p-8 shadow-luxury transition-all duration-300 hover:-translate-y-1 hover:shadow-luxury-lg flex flex-col"
            style={{ borderColor: "rgba(7,26,45,0.08)" }}
            data-testid="provider-benefit-customers"
          >
            <div
              className="w-12 h-12 rounded-2xl inline-flex items-center justify-center"
              style={{ background: `${GOLD}22`, color: "#8a6a2e" }}
            >
              <Users size={22} />
            </div>
            <h3 className="font-serif text-2xl mt-5" style={{ color: NAVY }}>
              Premium Customers
            </h3>
            <p
              className="mt-3 text-sm leading-relaxed"
              style={{ color: "rgba(7,26,45,0.72)" }}
            >
              Connect with customers looking for personalized travel,
              lifestyle, and unforgettable experiences.
            </p>

            <div className="mt-6 flex flex-wrap gap-2">
              {["Travel", "Lifestyle", "Celebrations", "Wellness", "Dining", "Events"].map(
                (tag) => (
                  <span
                    key={tag}
                    className="text-xs px-3 py-1.5 rounded-full border"
                    style={{
                      borderColor: "rgba(7,26,45,0.10)",
                      color: NAVY,
                      background: IVORY,
                    }}
                  >
                    {tag}
                  </span>
                )
              )}
            </div>
          </article>
        </div>
      </section>

      {/* ============== APPLICATION FLOW ============== */}
      <section
        className="py-16 sm:py-24"
        style={{ background: "rgba(7,26,45,0.03)" }}
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="max-w-2xl">
            <Eyebrow>The Application</Eyebrow>
            <h2
              className="font-serif mt-3 leading-tight"
              style={{ color: NAVY, fontSize: "clamp(1.9rem, 3.4vw, 2.75rem)" }}
            >
              Six steps from sign-up to your first booking.
            </h2>
            <p
              className="mt-4 text-base leading-relaxed"
              style={{ color: "rgba(7,26,45,0.65)" }}
            >
              Most providers are fully verified and live within 5–7 business
              days.
            </p>
          </div>

          <ol className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5" data-testid="provider-flow-list">
            {[
              {
                n: "01",
                t: "Create your Concierge profile",
                d: "Tell us who you are, what you can handle for customers, and the regions you serve.",
                i: IdCard,
              },
              {
                n: "02",
                t: "Add experiences, photos & pricing",
                d: "Build a luxury-grade Concierge profile with rich photography and your own rates.",
                i: Camera,
              },
              {
                n: "03",
                t: "Complete identity verification",
                d: "Government ID review handled securely through our partner.",
                i: BadgeCheck,
              },
              {
                n: "04",
                t: "Complete TREL Certified Training",
                d: "5-page onboarding course — free, takes about 10 minutes.",
                i: Shield,
              },
              {
                n: "05",
                t: "Receive your TREL Verified badge",
                d: "Display the trust mark customers look for across your profile.",
                i: Award,
              },
              {
                n: "06",
                t: "Start accepting bookings",
                d: "Go live on TREL and start earning — payouts after each completed booking.",
                i: CalendarCheck,
              },
            ].map(({ n, t, d, i: Icon }) => (
              <li
                key={n}
                className="rounded-3xl bg-white border p-6 sm:p-7 shadow-luxury transition-all duration-300 hover:-translate-y-0.5 hover:shadow-luxury-lg"
                style={{ borderColor: "rgba(7,26,45,0.08)" }}
                data-testid={`provider-flow-step-${n}`}
              >
                <div className="flex items-center justify-between">
                  <div
                    className="font-serif text-3xl"
                    style={{ color: GOLD }}
                  >
                    {n}
                  </div>
                  <div
                    className="w-10 h-10 rounded-xl inline-flex items-center justify-center"
                    style={{ background: `${EMERALD}10`, color: EMERALD }}
                  >
                    <Icon size={18} />
                  </div>
                </div>
                <h3
                  className="font-serif text-lg mt-4 leading-snug"
                  style={{ color: NAVY }}
                >
                  {t}
                </h3>
                <p
                  className="mt-2 text-sm leading-relaxed"
                  style={{ color: "rgba(7,26,45,0.65)" }}
                >
                  {d}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ============== TREL+ MEMBERSHIP ============== */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
        <div
          className="rounded-[32px] overflow-hidden border shadow-luxury-lg"
          style={{
            background: `linear-gradient(135deg, #FFFDF8 0%, #FFF8EE 60%, #FAEFD8 100%)`,
            borderColor: `${GOLD}55`,
          }}
          data-testid="provider-trel-plus-card"
        >
          <div className="grid lg:grid-cols-12 gap-8 p-7 sm:p-10 lg:p-14">
            {/* Left — pitch */}
            <div className="lg:col-span-7">
              <div className="flex items-center gap-3">
                <TrelPlusBadge size="md" />
                <span
                  className="text-[11px] uppercase tracking-[0.22em] font-semibold"
                  style={{ color: "rgba(7,26,45,0.55)" }}
                >
                  Optional premium membership
                </span>
              </div>
              <h2
                className="font-serif mt-4 leading-[1.05]"
                style={{ color: NAVY, fontSize: "clamp(2rem, 4vw, 3rem)" }}
              >
                TREL+ <span style={{ color: GOLD }}>Provider</span>
              </h2>
              <p
                className="mt-4 text-base sm:text-lg max-w-xl leading-relaxed"
                style={{ color: "rgba(7,26,45,0.72)" }}
              >
                A premium growth membership for providers who want enhanced
                visibility, growth tools, and featured placement across the TREL
                marketplace.
              </p>

              <div className="mt-6 inline-flex items-end gap-1">
                <span
                  className="font-serif"
                  style={{ color: NAVY, fontSize: "clamp(2.5rem, 5vw, 3.5rem)" }}
                >
                  $29
                </span>
                <span
                  className="pb-3 text-sm font-medium"
                  style={{ color: "rgba(7,26,45,0.65)" }}
                >
                  /month
                </span>
              </div>

              <div className="mt-7 flex flex-wrap items-center gap-3">
                <Link
                  to="/signup?role=provider&plan=plus"
                  className="btn-coral inline-flex items-center gap-2"
                  data-testid="provider-trel-plus-cta"
                >
                  Join TREL+ <Sparkles size={16} />
                </Link>
                <span
                  className="text-xs"
                  style={{ color: "rgba(7,26,45,0.55)" }}
                >
                  Separate from the TREL Certified Training (which is free)
                </span>
              </div>
            </div>

            {/* Right — benefits */}
            <div className="lg:col-span-5">
              <div
                className="rounded-2xl bg-white/70 backdrop-blur border p-6 sm:p-7"
                style={{ borderColor: `${GOLD}40` }}
              >
                <Eyebrow color={GOLD}>Member Benefits</Eyebrow>
                <ul className="mt-4 space-y-3 text-sm" style={{ color: NAVY }}>
                  {[
                    "Increased profile visibility",
                    "Premium TREL+ badge",
                    "Growth tools & promotions",
                    "Business insights & analytics",
                    "Featured placement opportunities",
                  ].map((b) => (
                    <li key={b} className="flex items-start gap-2.5">
                      <span
                        className="mt-0.5 w-5 h-5 rounded-full inline-flex items-center justify-center flex-shrink-0"
                        style={{ background: GOLD, color: "#3A2A0A" }}
                      >
                        <Check size={12} strokeWidth={3} />
                      </span>
                      <span style={{ color: "rgba(7,26,45,0.85)" }}>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Distinguishing badges */}
        <div
          className="mt-8 rounded-2xl border p-5 sm:p-6 bg-white"
          style={{ borderColor: "rgba(7,26,45,0.08)" }}
          data-testid="provider-badge-explainer"
        >
          <Eyebrow>Understanding the badges</Eyebrow>
          <div className="mt-3 grid sm:grid-cols-2 gap-4">
            <div className="flex items-start gap-3">
              <VerifiedBadge size="md" />
              <div className="text-sm" style={{ color: "rgba(7,26,45,0.75)" }}>
                <b style={{ color: NAVY }}>Trust & safety.</b> Identity reviewed
                and TREL Certified Training completed.
              </div>
            </div>
            <div className="flex items-start gap-3">
              <TrelPlusBadge size="md" />
              <div className="text-sm" style={{ color: "rgba(7,26,45,0.75)" }}>
                <b style={{ color: NAVY }}>Premium membership.</b> Optional growth
                program for active providers.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============== INDEPENDENT BUSINESSES DISCLOSURE ============== */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-12">
        <div
          className="rounded-2xl px-5 py-4 text-xs sm:text-sm border"
          style={{
            background: "rgba(0,174,239,0.06)",
            borderColor: "rgba(0,174,239,0.25)",
            color: "rgba(7,26,45,0.75)",
          }}
        >
          Concierges on TREL are independent businesses, not TREL employees.
          Verified concierges complete identity review and the TREL Certified
          Training before becoming active.
        </div>
      </section>

      {/* ============== FINAL CTA ============== */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-20 sm:pb-28">
        <div
          className="rounded-[28px] p-8 sm:p-12 lg:p-16 text-center relative overflow-hidden"
          style={{
            background: `linear-gradient(135deg, ${EMERALD} 0%, #0a6b53 60%, ${NAVY} 100%)`,
            color: IVORY,
          }}
        >
          <div className="relative z-10 max-w-3xl mx-auto">
            <Eyebrow color={GOLD}>Ready when you are</Eyebrow>
            <h2
              className="font-serif mt-3 leading-tight"
              style={{ fontSize: "clamp(1.9rem, 4vw, 3rem)", color: IVORY }}
            >
              Turn your craft into a thriving concierge business.
            </h2>
            <p
              className="mt-4 text-base sm:text-lg leading-relaxed"
              style={{ color: "rgba(255,248,238,0.82)" }}
            >
              Apply today and join hundreds of trusted TREL Concierges earning
              premium income across America.
            </p>
            <div className="mt-7 flex flex-wrap justify-center items-center gap-3">
              <Link
                to="/signup?role=provider"
                className="btn-coral inline-flex items-center gap-2 text-base"
                data-testid="provider-final-cta"
              >
                Start your application <ArrowRight size={18} />
              </Link>
              <Link
                to="/how-it-works#providers"
                className="text-sm font-semibold underline-offset-4 hover:underline"
                style={{ color: "rgba(255,248,238,0.85)" }}
              >
                Learn more about TREL
              </Link>
            </div>

            <div className="mt-8 flex flex-wrap justify-center gap-x-6 gap-y-3 text-xs" style={{ color: "rgba(255,248,238,0.7)" }}>
              <div className="inline-flex items-center gap-1.5"><TrendingUp size={13}/> 80% payout + 100% tips</div>
              <div className="inline-flex items-center gap-1.5"><Shield size={13}/> TREL Certified network</div>
              <div className="inline-flex items-center gap-1.5"><Award size={13}/> Premium customer base</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
